import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CuratedCollection from './CuratedCollection';
import NewOnHikeCinema from './NewOnHikeCinema';
import ComingSoon from './ComingSoon';
import TrailerRanking from './TrailerRanking';
import { usePermission } from '../../hooks/usePermission';
import { PERM } from '../../config/permissions';

/**
 * /curated — one tab per curated home rail. Each tab is labelled with its rail's
 * saved name (reported up by the tab itself), so a rename shows here at once.
 *
 * Every tab stays mounted and are only hidden, so switching tabs never throws
 * away the Trending tab's unsaved edits. The active tab lives in `?tab=` so it
 * survives a reload and can be linked to. A tab the role can't view is neither
 * shown nor mounted, so it never fires a request that would 403.
 */

const TABS = [
    { key: 'trending', icon: 'fas fa-fire', fallback: 'Trending', permission: PERM.SERIES },
    { key: 'new-on-hike-cinema', icon: 'fas fa-bolt', fallback: 'New on Hike Cinema', permission: PERM.SERIES },
    { key: 'coming-soon', icon: 'fas fa-clock', fallback: 'Coming Soon', permission: PERM.SERIES },
    { key: 'trailer-ranking', icon: 'fas fa-list-ol', fallback: 'Trailer Ranking', permission: PERM.TRAILER_RANKING },
];

const CurationTabs = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { canView } = usePermission();
    const tabs = TABS.filter((t) => canView(t.permission));
    const allowed = (key) => tabs.some((t) => t.key === key);
    const requested = searchParams.get('tab');
    const active = allowed(requested) ? requested : tabs[0]?.key;

    const [labels, setLabels] = useState({});
    const onTrendingName = useCallback((name) => setLabels((prev) => ({ ...prev, trending: name })), []);
    const onNewOnHikeName = useCallback((name) => setLabels((prev) => ({ ...prev, 'new-on-hike-cinema': name })), []);
    const onComingSoonName = useCallback((name) => setLabels((prev) => ({ ...prev, 'coming-soon': name })), []);

    const selectTab = (key) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', key);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="cct-page">
            <div className="cct-tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active === tab.key}
                        className={`cct-tab${active === tab.key ? ' is-active' : ''}`}
                        onClick={() => selectTab(tab.key)}
                    >
                        <i className={`${tab.icon} me-2`}></i>
                        {labels[tab.key]?.trim() || tab.fallback}
                    </button>
                ))}
            </div>

            {allowed('trending') && (
                <div role="tabpanel" hidden={active !== 'trending'}>
                    <CuratedCollection onNameChange={onTrendingName} />
                </div>
            )}
            {allowed('new-on-hike-cinema') && (
                <div role="tabpanel" hidden={active !== 'new-on-hike-cinema'}>
                    <NewOnHikeCinema onNameChange={onNewOnHikeName} />
                </div>
            )}
            {allowed('coming-soon') && (
                <div role="tabpanel" hidden={active !== 'coming-soon'}>
                    <ComingSoon onNameChange={onComingSoonName} />
                </div>
            )}
            {allowed('trailer-ranking') && (
                <div role="tabpanel" hidden={active !== 'trailer-ranking'}>
                    <TrailerRanking />
                </div>
            )}

            <style>{`
                .cct-tabs {
                    display: inline-flex; gap: 6px; flex-wrap: wrap; max-width: 100%;
                    padding: 5px; margin-bottom: 20px;
                    background: var(--glass-bg); border: 1px solid var(--border-color);
                    border-radius: var(--radius-md);
                }
                .cct-tab {
                    padding: 8px 18px; border: none; background: transparent;
                    color: var(--text-color); font-weight: 500; font-size: 14px;
                    border-radius: var(--radius-sm); transition: background 0.2s, color 0.2s;
                    max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .cct-tab:hover:not(.is-active) { color: var(--body-text-color); background: var(--sidebar-hover); }
                .cct-tab.is-active { background: var(--first-color); color: #fff; }
            `}</style>
        </div>
    );
};

export default CurationTabs;
